import { FC, useEffect, useState } from 'react';
import { Bar } from 'components/Header/Bar';
import { Footer } from 'components/Footer';
import { useAppSelector } from 'helpers/hooks';
import { getLandingConfig } from 'selectors/siteConfig';

const GOAL = 100_000_000;
const FALLBACK_AMOUNT = 99_482_640;

const formatDollars = (n: number) => `$${Math.round(n).toLocaleString()}`;

const getRandomDeduction = () => {
  const r = Math.random();
  if (r < 0.75) return Math.floor(10 + Math.random() * 90);
  if (r < 0.95) return Math.floor(100 + Math.random() * 400);
  return Math.floor(500 + Math.random() * 497);
};

export const HundredMScreen: FC = () => {
  const amountConfig = useAppSelector(state => getLandingConfig(state, '100m_amount'));
  const [currentRaised, setCurrentRaised] = useState(FALLBACK_AMOUNT);

  useEffect(() => {
    if (amountConfig?.amount_raised != null) {
      setCurrentRaised(Number(amountConfig.amount_raised));
    }
  }, [amountConfig?.amount_raised]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (Math.random() < 0.05) {
        setCurrentRaised(prev => {
          if (prev >= GOAL) return prev;
          return Math.min(prev + getRandomDeduction(), GOAL);
        });
      }
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  const remaining = Math.max(GOAL - currentRaised, 0);

  return (
    <div className="hm-page">
      <Bar />
      <section className="hm-hero">
        <div>
          <p className="hm-hero-eyebrow">
            The $100M Milestone &nbsp;|&nbsp; A Movement, Not a Moment
          </p>
          <h1 className="hm-hero-title">A $100 million movement.</h1>
          <p className="hm-hero-body">
            Four years ago, Oath started a movement. Donors at all levels have joined us – choosing
            strategy over noise, and directing every dollar to the races where it mattered most.
          </p>
        </div>
        <div className="hm-counter-box">
          <p className="hm-counter-eyebrow">The Countdown Is On</p>
          <div className="hm-counter-number">{formatDollars(currentRaised)}</div>
          <div className="hm-counter-grid">
            <div>
              <p className="hm-counter-remaining-label">Remaining to Goal</p>
              <p className="hm-counter-remaining-amount">{formatDollars(remaining)}</p>
            </div>
            <div>
              <p className="hm-counter-remaining-label">Donations Needed ($80 Avg)</p>
              <p className="hm-counter-remaining-amount">
                {Math.round(remaining / 80).toLocaleString()}
              </p>
            </div>
          </div>
          <a
            href="https://oath.vote/set?p=top-issues&ref=100m"
            className="hm-btn-gold"
            target="_blank"
            rel="noopener noreferrer"
          >
            Help Us Hit $100M
          </a>
        </div>
      </section>

      <div className="hm-ticker">
        <div className="hm-ticker-track">
          <span className="hm-ticker-item">500,000+ donations to State Leg</span>
          <span className="hm-ticker-sep" />
          <span className="hm-ticker-item">Held WI Supreme Court</span>
          <span className="hm-ticker-sep" />
          <span className="hm-ticker-item">Flipped 2 GA statewide seats</span>
          <span className="hm-ticker-sep" />
          <span className="hm-ticker-item">
            1,200,000+ donations to 2000+ candidates and organizations
          </span>
          <span className="hm-ticker-sep" />
          <span className="hm-ticker-item">500,000+ donations to State Leg</span>
          <span className="hm-ticker-sep" />
          <span className="hm-ticker-item">Held WI Supreme Court</span>
          <span className="hm-ticker-sep" />
          <span className="hm-ticker-item">Flipped 2 GA statewide seats</span>
          <span className="hm-ticker-sep" />
          <span className="hm-ticker-item">
            1,200,000+ donations to 2000+ candidates and organizations
          </span>
          <span className="hm-ticker-sep" />
        </div>
      </div>

      <section className="hm-stats-section">
        <div>
          <p className="hm-stats-eyebrow">How Oath Targets Smarter</p>
          <h2 className="hm-stats-title">Oath has helped more than 2,000 campaigns since 2022</h2>
          <p className="hm-stats-body">
            Most political money is wasted on safe seats and lost causes. Oath donors concentrate
            dollars where a few votes can swing the outcome – and the numbers prove it.
          </p>
        </div>
        <div>
          <p className="hm-chart-label">% of Dollars Directed</p>
          <div className="hm-bar-row">
            <span className="hm-bar-row-label">Decided by &lt; 1%</span>
            <div className="hm-bar-track">
              <div className="hm-bar-fill hm-bar-fill-1" style={{ width: '12%' }}>
                <span className="hm-bar-pct">12%</span>
              </div>
            </div>
          </div>
          <div className="hm-bar-row">
            <span className="hm-bar-row-label">Decided by &lt; 2.5%</span>
            <div className="hm-bar-track">
              <div className="hm-bar-fill hm-bar-fill-2" style={{ width: '43%' }}>
                <span className="hm-bar-pct">43%</span>
              </div>
            </div>
          </div>
          <div className="hm-bar-row">
            <span className="hm-bar-row-label">Decided by &lt; 5%</span>
            <div className="hm-bar-track">
              <div className="hm-bar-fill hm-bar-fill-3" style={{ width: '67%' }}>
                <span className="hm-bar-pct">67%</span>
              </div>
            </div>
          </div>
          <div className="hm-bar-row">
            <span className="hm-bar-row-label">Decided by &lt; 7.5%</span>
            <div className="hm-bar-track">
              <div className="hm-bar-fill hm-bar-fill-4" style={{ width: '84%' }}>
                <span className="hm-bar-pct">84%</span>
              </div>
            </div>
          </div>
          <div className="hm-bar-row">
            <span className="hm-bar-row-label">Decided by &lt; 10%</span>
            <div className="hm-bar-track">
              <div className="hm-bar-fill hm-bar-fill-5" style={{ width: '91%' }}>
                <span className="hm-bar-pct">91%</span>
              </div>
            </div>
          </div>
          <div className="hm-chart-callout">
            91% of funding has gone to races decided by less than a 10% margin
          </div>
        </div>
      </section>

      <section className="hm-wins-section">
        <p className="hm-wins-eyebrow">The Wins / The Receipts</p>
        <h2 className="hm-wins-title">Where YOU donated $100,000,000:</h2>
        <p className="hm-wins-subtitle">
          A non-exhaustive victory lap through races that could have ended differently without Oath
          donors. Close margins. Big investment. Huge wins.
        </p>

        <div className="hm-race-row">
          <div className="hm-race-card">
            <div className="hm-race-card-header">
              <div className="hm-race-tags">
                <span className="hm-tag hm-tag-gold">Flipping the House</span>
                <span className="hm-tag hm-tag-outline">2026</span>
                <span className="hm-tag hm-tag-outline">Federal</span>
              </div>
              <img
                src="https://storage.googleapis.com/oath_1/100m/card_1.png"
                alt="US Capitol"
                className="hm-race-card-photo"
              />
            </div>
            <h3 className="hm-race-card-title">
              Delivered double digit boost to flip the House in the midterms and{' '}
              <span className="hm-gold">block Trump's agenda</span>
            </h3>
            <p className="hm-race-card-body">
              The Oath community is the largest funder of flippable House races outside the
              Democratic party. In Q1 candidates got up to 50% of their funding through Oath.
            </p>
            <div className="hm-race-stats">
              <div className="hm-race-stat">
                <div className="hm-race-stat-num">3</div>
                <div className="hm-race-stat-label">seats to flip</div>
              </div>
              <div className="hm-race-stat">
                <div className="hm-race-stat-num">35%+</div>
                <div className="hm-race-stat-label">of funding coming from Oath</div>
              </div>
            </div>
            <div className="hm-race-share-label">Oath Share of Donations</div>
            <div className="hm-race-share-bar">
              <div className="hm-race-share-fill" style={{ width: '50%' }} />
            </div>
          </div>
          <div className="hm-stat-block">
            <div className="hm-stat-amount">$20M</div>
            <div className="hm-stat-desc">To Taking Back the House in 2026</div>
          </div>
        </div>

        <div className="hm-race-row hm-reversed">
          <div className="hm-race-card">
            <div className="hm-race-card-header">
              <div className="hm-race-tags">
                <span className="hm-tag hm-tag-gold">Voter Registration</span>
                <span className="hm-tag hm-tag-outline">2026</span>
                <span className="hm-tag hm-tag-outline">12+ States and Counting</span>
              </div>
              <img
                src="https://storage.googleapis.com/oath_1/100m/card_2.png"
                alt="Voter Registration"
                className="hm-race-card-photo"
              />
            </div>
            <h3 className="hm-race-card-title">
              Expanded registration efforts to{' '}
              <span className="hm-gold">win up and down ballot</span> in hard to win places
            </h3>
            <p className="hm-race-card-body">
              Through partisan efforts and nonprofit registration initiatives, Oath donors have
              helped register hundreds of thousands of voters which expand the map and set the
              foundation for more wins.
            </p>
          </div>
          <div className="hm-stat-block">
            <div className="hm-stat-amount">$5M</div>
            <div className="hm-stat-desc">To Registering New Voters</div>
          </div>
        </div>

        <div className="hm-race-row">
          <div className="hm-race-card">
            <div className="hm-race-card-header">
              <div className="hm-race-tags">
                <span className="hm-tag hm-tag-gold">Big Wins in NC</span>
                <span className="hm-tag hm-tag-outline">2024</span>
                <span className="hm-tag hm-tag-outline">North Carolina</span>
              </div>
              <img
                src="https://storage.googleapis.com/oath_1/100m/card_3.png"
                alt="Dante Pittman"
                className="hm-race-card-photo"
              />
            </div>
            <h3 className="hm-race-card-title">
              <span className="hm-gold">Broke the Republican super majority in North Carolina</span>{' '}
              by supporting candidates like Dante Pittman
            </h3>
            <p className="hm-race-card-body">
              One race in NC helped restore veto power to the Democratic Governor. It came down to
              just 870 votes and may have ended differently if not for hundreds of thousands of
              dollars from Oath donors.
            </p>
            <div className="hm-race-stats">
              <div className="hm-race-stat">
                <div className="hm-race-stat-num">870</div>
                <div className="hm-race-stat-label">Vote Margin</div>
              </div>
              <div className="hm-race-stat">
                <div className="hm-race-stat-num">30%</div>
                <div className="hm-race-stat-label">Of Donors from Oath</div>
              </div>
            </div>
            <div className="hm-race-share-label">Oath Share of Donations</div>
            <div className="hm-race-share-bar">
              <div className="hm-race-share-fill" />
            </div>
          </div>
          <div className="hm-stat-block">
            <div className="hm-stat-amount">$18M</div>
            <div className="hm-stat-desc">To Flipping Swing State Legislatures</div>
          </div>
        </div>

        <div className="hm-race-row hm-reversed">
          <div className="hm-race-card">
            <div className="hm-race-card-header">
              <div className="hm-race-tags">
                <span className="hm-tag hm-tag-gold">Ballot Measures</span>
                <span className="hm-tag hm-tag-outline">2023</span>
                <span className="hm-tag hm-tag-outline">Ohio</span>
              </div>
              <img
                src="https://storage.googleapis.com/oath_1/100m/card_4.png"
                alt="Ohioans for Reproductive Freedom"
                className="hm-race-card-photo"
              />
            </div>
            <h3 className="hm-race-card-title">
              <span className="hm-gold">Reversed a 6 week abortion ban</span> in Ohio
            </h3>
            <p className="hm-race-card-body">
              Oath donors helped Ohioans for Reproductive Freedom put a critical ballot measure on
              the ballot which passed with 10%+ support, a feat for a typically conservative state.
            </p>
          </div>
          <div className="hm-stat-block">
            <div className="hm-stat-amount">$2M</div>
            <div className="hm-stat-desc">To Save Repro Rights in Red States</div>
          </div>
        </div>

        <div className="hm-race-row">
          <div className="hm-race-card">
            <div className="hm-race-card-header">
              <div className="hm-race-tags">
                <span className="hm-tag hm-tag-gold">Attorney General</span>
                <span className="hm-tag hm-tag-outline">2022</span>
                <span className="hm-tag hm-tag-outline">Arizona</span>
              </div>
              <img
                src="https://storage.googleapis.com/oath_1/100m/card_5.png"
                alt="Kris Mayes"
                className="hm-race-card-photo"
              />
            </div>
            <h3 className="hm-race-card-title">
              <span className="hm-gold">Flipped the Arizona Attorney General</span> race by 280
              votes
            </h3>
            <p className="hm-race-card-body">
              With over 2.5 million votes cast, this critical statewide race came down to just 280
              votes to elect Kris Mayes. She has used her authority to repeal a 1864 near-total
              abortion ban and much more.
            </p>
            <div className="hm-race-stats">
              <div className="hm-race-stat">
                <div className="hm-race-stat-num">280</div>
                <div className="hm-race-stat-label">Vote Margin — out of 2,540,084 cast</div>
              </div>
              <div className="hm-race-stat">
                <div className="hm-race-stat-num">10%</div>
                <div className="hm-race-stat-label">Of Donors from Oath</div>
              </div>
            </div>
            <div className="hm-race-share-label">Oath Share of Donations</div>
            <div className="hm-race-share-bar">
              <div className="hm-race-share-fill" style={{ width: '10%' }} />
            </div>
          </div>
          <div className="hm-stat-block">
            <div className="hm-stat-amount">$7M</div>
            <div className="hm-stat-desc">To Overlooked Statewide Races</div>
          </div>
        </div>
      </section>

      <section className="hm-thankyou-section">
        <h2 className="hm-thankyou-title">& so much more. Thank you!</h2>
        <p className="hm-thankyou-body">
          Democratic donors are choosing strategy over noise. Tens of thousands of donors,
          campaigns, and political organizations are actively choosing Oath — building a movement
          that grows every cycle.
        </p>
        <div className="hm-feature-cards">
          <div className="hm-feature-card">
            <img
              src="https://storage.googleapis.com/oath_1/100m/strategy.png"
              alt=""
              className="hm-feature-icon"
            />
            <h3 className="hm-feature-title">Strategy, not spam</h3>
            <p className="hm-feature-body">
              Oath's Impact Score analyzes thousands of races – so your dollars go where they'll
              actually move the needle.
            </p>
          </div>
          <div className="hm-feature-card">
            <img
              src="https://storage.googleapis.com/oath_1/100m/yourdata.png"
              alt=""
              className="hm-feature-icon"
            />
            <h3 className="hm-feature-title">Your data stays yours</h3>
            <p className="hm-feature-body">
              Oath never sells or shares donor contact information. No spam texts. No barrage of
              fundraising emails. Just your impact.
            </p>
          </div>
          <div className="hm-feature-card">
            <img
              src="https://storage.googleapis.com/oath_1/100m/growingmovement.png"
              alt=""
              className="hm-feature-icon"
            />
            <h3 className="hm-feature-title">A growing movement</h3>
            <p className="hm-feature-body">
              As the 2026 cycle accelerates, our community of strategic donors is expanding – every
              new member makes the movement more powerful.
            </p>
          </div>
        </div>
      </section>

      <section className="hm-cta-section">
        <div className="hm-cta-watermark">100M</div>
        <p className="hm-cta-eyebrow">What Comes Next</p>
        <h2 className="hm-cta-title">
          Now let's make it
          <br />
          <span className="hm-gold">$200 million.</span>
        </h2>
        <p className="hm-cta-body">
          The 2026 election cycle is already underway. Join the movement of strategic donors and
          help us double what we've built together.
        </p>
        <a
          href="https://oath.vote/login?ref=100m"
          className="hm-btn-gold hm-btn-relative"
          target="_blank"
          rel="noopener noreferrer"
        >
          Join Oath Today
        </a>
      </section>

      <Footer />
    </div>
  );
};
